/**
 * Portal Page — Decision Map UI
 *
 * Route: /portal/view/:docId
 *
 * Loads a persisted document and renders the four-region portal:
 *   A. Header (property summary, confidence)
 *   B. Core Decision Axis (A vs A+ + Option B alternative)
 *   C. Dual SWOT Grid (status quo vs recommended)
 *   D. Evidence & Assumptions Panel
 *
 * Plus parallel Heating and Water Services sections.
 *
 * Non-linear, explorable, transparent.
 * No pricing. No brands. No marketing.
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useRecommendation } from '../context/RecommendationContext';
import { ExpertViewProvider } from '../context/ExpertViewContext';
import { retrieveDocument } from '../utils/documentPersistence';
import type { StorySnapshot } from '../utils/storySnapshot';
import { buildPortalViewModel, type PortalViewModel, type OptionSlots } from '../components/portal/buildPortalViewModel';
import { PortalHeader } from '../components/portal/PortalHeader';
import { DecisionAxis } from '../components/portal/DecisionAxis';
import { DualSwotGrid } from '../components/portal/DualSwotGrid';
import { EvidencePanel } from '../components/portal/EvidencePanel';
import { HeatingSection } from '../components/portal/HeatingSection';
import { WaterSection } from '../components/portal/WaterSection';
import { PortalWowSummary } from '../components/portal/PortalWowSummary';
import type { SystemRecOutput } from '@core/types';
import { updateRawInputSnapshot, trackLifecycleEvent, getDiagnosticsState } from '../mapping';
import { sanitizeDocId } from '../utils/docId';
import '../components/portal/Portal.css';

export default function PortalPage() {
  const { docId: rawDocId } = useParams<{ docId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { collectedData, recommendationResult } = useRecommendation();

  // Sanitize the docId from the URL to strip trailing punctuation / whitespace
  const docId = useMemo(() => {
    if (!rawDocId) return undefined;
    const cleaned = sanitizeDocId(rawDocId);
    if (cleaned !== rawDocId) {
      console.warn('[portal] docId sanitized', { raw: rawDocId, cleaned, wasSanitized: true });
    }
    return cleaned ?? undefined;
  }, [rawDocId]);

  // Detect invalid docId early (raw value present but fails sanitization)
  const isInvalidDocId = !!rawDocId && !docId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<'not_found' | 'server' | 'invalid_link' | 'generic' | null>(null);
  const [output, setOutput] = useState<SystemRecOutput | null>(null);
  const [optionSlots, setOptionSlots] = useState<OptionSlots | undefined>(undefined);
  const [retryCount, setRetryCount] = useState(0);

  // Track whether dev mode is enabled via ?dev=1
  const isDevMode = new URLSearchParams(location.search).get('dev') === '1';

  // ─── Data loading ──────────────────────────────────────────────

  // Keep a ref to avoid stale closure in the retry handler
  const retryCountRef = useRef(retryCount);
  retryCountRef.current = retryCount;

  useEffect(() => {
    let cancelled = false;

    // If the raw docId is present but sanitization failed, show invalid-link UI immediately
    if (isInvalidDocId) {
      setLoading(false);
      setError('The link appears to be invalid or incomplete. Please re-scan the QR code or request a new link.');
      setErrorKind('invalid_link');
      console.error('[portal] Invalid docId in URL', { rawDocId });
      return;
    }

    async function loadData() {
      setLoading(true);
      setError(null);
      setErrorKind(null);

      // Path 1: Load from docId in URL
      if (docId) {
        const fetchUrl = `/api/story/doc/${docId}`;
        console.log('[portal] Loading document', { docId, wasSanitized: docId !== rawDocId, fetchUrl, retry: retryCountRef.current });

        try {
          const doc = await retrieveDocument(docId);
          if (cancelled) return;

          console.log('[portal] Document loaded successfully', { docId });

          if (doc.engineOutput?.output) {
            setOutput(doc.engineOutput.output);
            if (doc.optionSlots) {
              setOptionSlots(doc.optionSlots);
            }

            // Push raw input into mappingStore to trigger normalization
            // This ensures DevOverlay and other dev tools can see the canonical normalized data
            // Type assertion is safe: doc.rawInput is StorySnapshot['rawInput'] which is structurally
            // compatible with Record<string, unknown> expected by updateRawInputSnapshot
            if (doc.rawInput && getDiagnosticsState().enabled) {
              updateRawInputSnapshot(doc.rawInput as unknown as Record<string, unknown>, {
                docId,
                contractVersion: (doc as StorySnapshot).contractVersion ?? null,
                migratedFromLegacy: (doc as StorySnapshot).migratedFromLegacy ?? false,
              });
              trackLifecycleEvent('doc_loaded', 'PortalPage:loadData', {
                docId,
                keys: Object.keys(doc.rawInput).length,
                note: 'Document loaded and pushed to mappingStore',
              });
            }
          } else {
            setError('Insufficient information to display recommendations. The assessment may be incomplete or missing critical data (such as cold mains performance). Please complete the assessment in Site-Visit Mode and ensure all required fields are assessed.');
            setErrorKind('generic');
          }
        } catch (err) {
          if (cancelled) return;
          const message = err instanceof Error ? err.message : 'Failed to load document';
          const statusMatch = message.match(/\b(404|500|503)\b/);
          const statusCode = statusMatch ? parseInt(statusMatch[1], 10) : null;

          console.error('[portal] Document load failed', {
            docId,
            fetchUrl,
            statusCode,
            error: message,
          });

          if (statusCode === 404) {
            setError('Report not found. The link may be incomplete. Please re-open the QR code or request a new link.');
            setErrorKind('not_found');
          } else if (statusCode && statusCode >= 500) {
            setError('Temporary issue loading the report. Please try again.');
            setErrorKind('server');
          } else {
            setError(message);
            setErrorKind('generic');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      // Path 2: Use in-memory recommendation from context
      if (recommendationResult?.output?.primary) {
        setOutput(recommendationResult.output);
        setLoading(false);

        // Push in-memory data into mappingStore
        // Type assertion is safe: collectedData is CollectedData which is structurally
        // compatible with Record<string, unknown> expected by updateRawInputSnapshot
        if (collectedData && getDiagnosticsState().enabled) {
          updateRawInputSnapshot(collectedData as unknown as Record<string, unknown>);
          trackLifecycleEvent('context_data_loaded', 'PortalPage:loadData', {
            keys: Object.keys(collectedData).length,
            note: 'In-memory context data pushed to mappingStore',
          });
        }
        return;
      }

      // Path 3: No data available — redirect to survey
      setError('No recommendation data available. Please complete a property assessment in Site-Visit Mode first, ensuring all critical information (including cold mains performance) is assessed. Then return here to view the decision map.');
      setErrorKind('generic');
      setLoading(false);
    }

    loadData();
    return () => { cancelled = true; };
  }, [docId, rawDocId, isInvalidDocId, recommendationResult, collectedData, retryCount]);

  // ─── Build view model ──────────────────────────────────────────

  const viewModel = useMemo<PortalViewModel | null>(() => {
    if (!output) return null;
    try {
      // Pass null for data parameter to force buildPortalViewModel to use
      // output.inputEcho (normalized input) instead of raw CollectedData.
      // This ensures the portal UI reflects normalized values from the engine.
      return buildPortalViewModel(output, null, undefined, optionSlots);
    } catch (err) {
      console.error('Failed to build portal view model:', err);
      setError('Failed to process recommendation data.');
      setErrorKind('generic');
      return null;
    }
  }, [output, optionSlots]);

  // ─── Cross-linking state ───────────────────────────────────────

  const [highlightedSwotNode, setHighlightedSwotNode] = useState<string | null>(null);

  const handleSwotHighlight = useCallback((nodeId: string) => {
    setHighlightedSwotNode(prev => prev === nodeId ? null : nodeId);
  }, []);

  const handleSwotNodeClick = useCallback((nodeId: string) => {
    setHighlightedSwotNode(prev => prev === nodeId ? null : nodeId);
  }, []);

  const handleEvidenceClick = useCallback((_itemId: string) => {
    // Future: highlight SWOT nodes that depend on this evidence item
  }, []);

  const handleRetry = useCallback(() => {
    setRetryCount(c => c + 1);
  }, []);

  // ─── Render ────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="portal-shell">
        <div className="portal-shell__loading" role="status" aria-label="Loading">
          Loading assessment...
        </div>
      </div>
    );
  }

  if (error || !viewModel) {
    const canRetry = errorKind === 'server' || errorKind === 'generic';
    const isInvalidLink = errorKind === 'invalid_link';

    return (
      <div className="portal-shell">
        <div className="portal-shell__error" role="alert">
          <p>{error || 'Unable to display assessment.'}</p>

          {isInvalidLink && (
            <p className="portal-shell__error-hint">
              Please re-scan the QR code or request a new link from your installer.
            </p>
          )}

          <div className="portal-shell__error-actions">
            {canRetry && (
              <button
                onClick={handleRetry}
                className="portal-header__action"
              >
                Retry
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className="portal-header__action"
            >
              Start new assessment
            </button>
          </div>

          {isDevMode && (
            <details className="portal-shell__debug">
              <summary>Debug info</summary>
              <pre style={{ fontSize: '0.75rem', overflowX: 'auto' }}>
                {JSON.stringify({ rawDocId, docId, errorKind }, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  return (
    <ExpertViewProvider>
      <div className="portal-shell">
        {/* A. Header */}
        <PortalHeader data={viewModel.header} docId={docId} />

        <div className="portal-shell__body">
          <PortalWowSummary output={output} viewModel={viewModel} />

          {/* B. Core Decision Axis */}
          <div id="decision-axis-anchor">
            <DecisionAxis
              data={viewModel.decisionAxis}
              onSwotHighlight={handleSwotHighlight}
            />
          </div>

          {/* Heating & Water Services (parallel, separate) */}
          <div className="portal-shell__systems">
            <HeatingSection data={viewModel.heating} />
            <WaterSection data={viewModel.waterServices} />
          </div>

          {/* C. Dual SWOT Grid */}
          <div id="swot-anchor">
            <DualSwotGrid
              data={viewModel.swot}
              highlightedNodeId={highlightedSwotNode}
              onNodeClick={handleSwotNodeClick}
            />
          </div>

          {/* D. Evidence & Assumptions Panel */}
          <div id="evidence-anchor">
            <EvidencePanel
              items={viewModel.evidence}
              onItemClick={handleEvidenceClick}
            />
          </div>
        </div>
      </div>
    </ExpertViewProvider>
  );
}
